import { createFileRoute, Link } from "@tanstack/react-router";
import { Smartphone, Zap, Bell, Cloud, Fingerprint, RefreshCw, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/services/mobile-application/")({
  component: MobileIndex,
  head: () => ({
    meta: [
      { title: "Mobile App Development — CoderLity" },
      { name: "description", content: "Native-like mobile apps for iOS and Android built with React Native and Expo." },
      { property: "og:title", content: "Mobile App Development — CoderLity" },
      { property: "og:description", content: "Cross-platform mobile apps that feel native, ship faster and update over the air." },
      { property: "og:url", content: "/services/mobile-application" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/services/mobile-application" }],
  }),
});

const platforms = [
  { slug: "ios-swift", title: "iOS (Swift)", desc: "Explore ios (swift)" },
  { slug: "android-kotlin", title: "Android (Kotlin)", desc: "Explore android (kotlin)" },
  { slug: "react-native", title: "React Native", desc: "Explore react native" },
  { slug: "flutter", title: "Flutter", desc: "Explore flutter" },
];

const appTypes = [
  { slug: "on-demand-apps", title: "On-demand Apps", desc: "Explore on-demand apps" },
  { slug: "marketplace-apps", title: "Marketplace Apps", desc: "Explore marketplace apps" },
  { slug: "fintech-apps", title: "Fintech Apps", desc: "Explore fintech apps" },
  { slug: "social-apps", title: "Social Apps", desc: "Explore social apps" },
];

const lifecycle = [
  { slug: "app-strategy", title: "App Strategy", desc: "Explore app strategy" },
  { slug: "uiux-prototyping", title: "UI/UX & Prototyping", desc: "Explore ui/ux & prototyping" },
  { slug: "publishing-aso", title: "Publishing & ASO", desc: "Explore publishing & aso" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/services/mobile-application/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function MobileIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Platforms" items={platforms} />
        <Group title="App Types" items={appTypes} />
        <Group title="Lifecycle" items={lifecycle} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Services",
  title: "Mobile Application",
  sub: "Native-like mobile apps for iOS and Android.",
  intro:
    "We ship cross-platform mobile apps that feel indistinguishable from native — buttery animations, native gestures, offline-first sync, and push notifications that actually arrive. One codebase, two app stores, fraction of the cost. From MVP to App Store launch in 8 weeks.",
  features: [
    { icon: Smartphone, title: "iOS + Android, one codebase", description: "React Native and Expo so every feature ships to both stores at once." },
    { icon: Zap, title: "Native performance", description: "60fps animations, hardware-accelerated lists, smooth gestures." },
    { icon: Bell, title: "Push notifications", description: "Targeted, scheduled and personalized — including rich media." },
    { icon: Cloud, title: "Offline-first", description: "Local-first data with seamless background sync when online." },
    { icon: Fingerprint, title: "Biometric auth", description: "Face ID, Touch ID and secure keystore integration." },
    { icon: RefreshCw, title: "Over-the-air updates", description: "Ship bug fixes in minutes without waiting for App Store review." },
  ],
  process: [
    { step: "01", title: "Strategy", description: "Platform priorities, user journeys and monetization model." },
    { step: "02", title: "Prototype", description: "Interactive prototype tested on real devices before code." },
    { step: "03", title: "Build", description: "Two-week sprints with builds installable via TestFlight." },
    { step: "04", title: "Submit & support", description: "App Store + Play Store submission and post-launch updates." },
  ],
  deliverables: [
    "Published iOS and Android apps",
    "Full source code with documentation",
    "Backend API and database",
    "Push notification + analytics integration",
    "App Store + Play Store listings and assets",
    "OTA update pipeline",
  ],
  techStack: ["React Native", "Expo", "TypeScript", "Swift (native modules)", "Kotlin (native modules)", "Firebase", "OneSignal", "Sentry", "Supabase"],
  pricing: [
    { name: "MVP", price: "$15k", features: ["Up to 8 screens", "Auth + basic backend", "Both app stores", "6–8 weeks"] },
    { name: "Full product", price: "$35k", period: "project", highlighted: true, features: ["20+ screens", "Payments + push", "Offline sync", "10–14 weeks"] },
    { name: "Retainer", price: "$8k", period: "month", features: ["Dedicated mobile team", "Bi-weekly releases", "OTA + bug fixes", "Performance tuning"] },
  ],
  faqs: [
    { q: "Native or cross-platform?", a: "We use React Native for 95% of features and drop into native Swift/Kotlin only when needed (e.g. complex camera, AR). You get native quality at cross-platform speed." },
    { q: "Will it pass App Store review?", a: "Yes. We follow Apple and Google's guidelines from day one and have a 100% first-submission approval rate for the past 3 years." },
    { q: "Can you update an existing app?", a: "Yes. We do feature work, performance audits, and full rewrites of legacy React Native or hybrid apps." },
    { q: "Who owns the code?", a: "You do — everything ships to your GitHub, your App Store account, and your backend." },
  ],
};
